import { Building, People } from '@procore/core-icons'
import { useId } from '@react-aria/utils'
import React, { forwardRef, useMemo, useState } from 'react'
import { Avatar } from '../Avatar'
import { Button } from '../Button'
import { ContactItem } from '../ContactItem'
import { Link } from '../Link'
import { Modal } from '../Modal'
import { OverlayTrigger } from '../OverlayTrigger'
import { Popover } from '../Popover'
import { Typography } from '../Typography'
import { useI18nContext } from '../_hooks/I18n'
import type { TriggerVariant } from '../_hooks/Trigger'
import type { Color } from '../_styles/colors'
import { mergeRefs } from '../_utils/mergeRefs'
import {
  colorsOrder,
  foldedItemsCap,
  restCountThreshold,
  visibleItemsCap,
} from './AvatarStack.constants'
import {
  StyledAvatar,
  StyledContactItem,
  StyledContactItems,
  StyledModalBody,
  StyledViewAllWrapper,
  StyledWrapper,
} from './AvatarStack.styles'
import type {
  AvatarStackItem,
  AvatarStackItemId,
  AvatarStackItemType,
  AvatarStackProps,
  AvatarStackSize,
  FoldedAvatarStackProps,
  ViewAllModalProps,
} from './AvatarStack.types'
import { useAvatarPopover } from './useAvatarPopover'

const OVERLAY_WRAPPER_SELECTOR =
  '[data-qa="core-overlay-trigger-overlay-wrapper"]'

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'

const POPOVER_TRIGGER = ['hover', 'focus'] as TriggerVariant[]

const POPOVER_SHOW_KEYS = ['Enter', ' ']

const POPOVER_HIDE_KEYS = {
  overlay: ['Escape', 'Esc'],
  target: ['Escape', 'Esc'],
}

export function getOverflowValues<Item extends AvatarStackItem>(items: Item[]) {
  let foldedItems: Item[] = []
  let restCountLabel: string | null = null
  let isViewAllNeeded = false

  const visibleItems =
    items.length > visibleItemsCap ? items.slice(0, visibleItemsCap - 1) : items
  const restItemsCount = items.length - visibleItems.length

  if (restItemsCount > 0) {
    foldedItems = items.slice(
      visibleItems.length,
      visibleItems.length + foldedItemsCap
    )

    restCountLabel =
      restItemsCount > restCountThreshold
        ? `${restCountThreshold}+`
        : `+${restItemsCount}`

    isViewAllNeeded = restItemsCount > foldedItemsCap
  }

  return {
    visibleItems,
    foldedItems,
    restCountLabel,
    isViewAllNeeded,
  }
}

export function getIcon(type: AvatarStackItemType, size: 'md' | 'sm') {
  switch (type) {
    case 'company':
      return <Building size={size} />

    case 'group':
      return <People size={size} />

    case 'user':
    default:
      return null
  }
}

function getContactIcon(type: AvatarStackItemType) {
  return getIcon(type, 'md')
}

export function getAvatarIcon(
  type: AvatarStackItemType,
  size: AvatarStackSize
) {
  const avatarSize = size === 'lg' ? 'md' : 'sm'
  return getIcon(type, avatarSize)
}

export function getColorOrder(avatarItems: AvatarStackItem[]) {
  return avatarItems.reduce(
    ({ map, color }, item) => {
      if (item.imageUrl) {
        return {
          map,
          color,
        }
      }

      if (item.inactive) {
        map.set(item.id, 'gray70')

        return {
          map,
          color,
        }
      }

      map.set(item.id, color)

      const currentColorIndex = colorsOrder.indexOf(color)
      const nextColorIndex = (currentColorIndex + 1) % colorsOrder.length
      return {
        map,
        color: item.imageUrl ? color : colorsOrder[nextColorIndex],
      }
    },
    {
      map: new Map<AvatarStackItemId, Color>(),
      color: colorsOrder[0],
    }
  ).map
}

export function AvatarStackContactItem({
  id,
  type,
  imageUrl,
  initials,
  inactive,
  name,
  linkUrl,
  description,
}: AvatarStackItem) {
  return (
    <StyledContactItem
      disabled={inactive}
      icon={getContactIcon(type)}
      imageUrl={imageUrl}
      initials={initials}
      key={id}
    >
      <ContactItem.Title>
        {!inactive && linkUrl ? <Link href={linkUrl}>{name}</Link> : name}
      </ContactItem.Title>
      <ContactItem.Description>
        {type === 'group' ? (
          <Typography color="gray15">{description}</Typography>
        ) : (
          description
        )}
      </ContactItem.Description>
    </StyledContactItem>
  )
}

export function AvatarContent({
  imageUrl,
  initials,
  type,
  size,
  ...props
}: {
  imageUrl?: AvatarStackItem['imageUrl']
  initials: AvatarStackItem['initials']
  type: AvatarStackItem['type']
  size: AvatarStackSize
}) {
  const avatarIcon = getAvatarIcon(type, size)

  if (imageUrl) {
    return <Avatar.Portrait {...props} imageUrl={imageUrl} />
  }

  if (avatarIcon) {
    return <Avatar.Icon {...props} icon={avatarIcon} />
  }

  if (initials) {
    return <Avatar.Label {...props}>{initials}</Avatar.Label>
  }

  return null
}

function AvatarWithPopover<Item extends AvatarStackItem>({
  item,
  size,
  colors,
}: {
  item: Item
  size: AvatarStackSize
  colors: Map<AvatarStackItemId, Color>
}) {
  const { triggerRef, popoverContentRef, beforeShow, beforeHide, afterHide } =
    useAvatarPopover({
      focusableSelector: FOCUSABLE_SELECTOR,
      overlayWrapperSelector: OVERLAY_WRAPPER_SELECTOR,
    })

  return (
    <OverlayTrigger
      key={item.id}
      trigger={POPOVER_TRIGGER}
      showKeys={POPOVER_SHOW_KEYS}
      hideKeys={POPOVER_HIDE_KEYS}
      restoreFocusOnHide={false}
      shrinkOverlay
      arrow
      trackAriaExpanded
      placement="top"
      beforeShow={(e) => {
        if (item.inactive) return false
        return beforeShow(e)
      }}
      beforeHide={beforeHide}
      afterHide={afterHide}
      overlay={
        <Popover.Content ref={popoverContentRef} placement="top">
          <AvatarStackContactItem
            id={item.id}
            type={item.type}
            imageUrl={item.imageUrl}
            initials={item.initials}
            inactive={item.inactive}
            name={item.name}
            linkUrl={item.linkUrl}
            description={item.description}
          />
        </Popover.Content>
      }
    >
      <StyledAvatar
        ref={triggerRef}
        role="button"
        $color={colors.get(item.id) as Color}
        aria-label={`${item.name}, ${item.description}`}
        disabled={item.inactive}
        size={size}
      >
        <AvatarContent
          imageUrl={item.imageUrl}
          initials={item.initials}
          type={item.type}
          size={size}
        />
      </StyledAvatar>
    </OverlayTrigger>
  )
}

export function FoldedAvatarStack<Item extends AvatarStackItem>({
  items,
  onClickViewAll,
  size,
  restCountLabel,
  isViewAllNeeded,
  title,
  viewMoreTriggerRef,
}: FoldedAvatarStackProps<Item>) {
  const I18n = useI18nContext()
  const restAvatarId = useId()
  const { triggerRef, popoverContentRef, beforeShow, beforeHide, afterHide } =
    useAvatarPopover({
      focusableSelector: FOCUSABLE_SELECTOR,
      overlayWrapperSelector: OVERLAY_WRAPPER_SELECTOR,
    })

  return (
    <OverlayTrigger
      aria-labelledby={restAvatarId}
      trigger={POPOVER_TRIGGER}
      showKeys={POPOVER_SHOW_KEYS}
      hideKeys={POPOVER_HIDE_KEYS}
      restoreFocusOnHide={false}
      shrinkOverlay
      arrow
      trackAriaExpanded
      placement="top"
      beforeShow={beforeShow}
      beforeHide={beforeHide}
      afterHide={afterHide}
      overlay={
        <Popover.Content ref={popoverContentRef} placement="top">
          <StyledContactItems>
            {items.map((item) => (
              <AvatarStackContactItem
                key={item.id}
                id={item.id}
                type={item.type}
                imageUrl={item.imageUrl}
                initials={item.initials}
                inactive={item.inactive}
                name={item.name}
                linkUrl={item.linkUrl}
                description={item.description}
              />
            ))}
          </StyledContactItems>
          {isViewAllNeeded && (
            <StyledViewAllWrapper>
              <Button
                data-qa="core-avatar-stack-view-all-modal-trigger"
                onClick={onClickViewAll}
                size="sm"
                variant="secondary"
              >
                {I18n.t('core.avatarStack.viewAll')}
              </Button>
            </StyledViewAllWrapper>
          )}
        </Popover.Content>
      }
    >
      <StyledAvatar
        ref={
          viewMoreTriggerRef
            ? mergeRefs(triggerRef, viewMoreTriggerRef)
            : triggerRef
        }
        role="button"
        tabIndex={0}
        id={restAvatarId}
        aria-label={`${restCountLabel}, ${title}`}
        data-qa="core-avatar-stack-folded-avatars-popover-trigger"
        size={size}
        $color="gray85"
      >
        <Avatar.Label aria-hidden>
          <Typography color="black">{restCountLabel}</Typography>
        </Avatar.Label>
      </StyledAvatar>
    </OverlayTrigger>
  )
}

export function ViewAllModal<Item extends AvatarStackItem>({
  isOpen,
  onClose,
  title,
  items,
}: ViewAllModalProps<Item>) {
  const I18n = useI18nContext()
  const labelId = useId()
  const text = `${title} (${items.length})`

  return (
    <Modal
      role="dialog"
      aria-labelledby={labelId}
      open={isOpen}
      onClose={onClose}
    >
      <Modal.Header onClose={onClose}>
        <Modal.Heading id={labelId}>{text}</Modal.Heading>
      </Modal.Header>
      <StyledModalBody>
        {items.map((item) => (
          <AvatarStackContactItem
            key={item.id}
            id={item.id}
            type={item.type}
            imageUrl={item.imageUrl}
            initials={item.initials}
            inactive={item.inactive}
            name={item.name}
            linkUrl={item.linkUrl}
            description={item.description}
          />
        ))}
      </StyledModalBody>
      <Modal.Footer>
        <Modal.FooterButtons>
          <Button onClick={onClose}>{I18n.t('core.avatarStack.close')}</Button>
        </Modal.FooterButtons>
      </Modal.Footer>
    </Modal>
  )
}

export function defaultInitials<Item extends AvatarStackItem>(item: Item) {
  return item.initials as string
}

export function defaultGetImageUrl<Item extends AvatarStackItem>(item: Item) {
  return item.imageUrl as string
}

export function getTransformedItems<Item extends AvatarStackItem>({
  items,
  getInitials,
  getImageUrl,
}: {
  items: Item[]
  getInitials: AvatarStackProps<Item>['getInitials']
  getImageUrl: AvatarStackProps<Item>['getImageUrl']
}) {
  return items.map((item) => ({
    ...item,
    initials: getInitials!(item),
    imageUrl: getImageUrl!(item),
  }))
}

const MODAL_CLOSE_FOCUS_DELAY_MS = 350

const _AvatarStack = <Item extends AvatarStackItem>(
  {
    items: _items,
    title,
    size = 'lg',
    getInitials = defaultInitials,
    getImageUrl = defaultGetImageUrl,
    onClickViewAll,
    ...props
  }: AvatarStackProps<Item>,
  ref: React.ForwardedRef<HTMLDivElement>
) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const viewMoreTriggerRef = React.useRef<HTMLDivElement>(null)
  const wasModalOpenRef = React.useRef(false)

  React.useEffect(() => {
    if (wasModalOpenRef.current && !isModalOpen) {
      const id = setTimeout(() => {
        viewMoreTriggerRef.current?.focus()
      }, MODAL_CLOSE_FOCUS_DELAY_MS)
      wasModalOpenRef.current = false
      return () => clearTimeout(id)
    }
    wasModalOpenRef.current = isModalOpen
  }, [isModalOpen])

  const items = useMemo(
    () =>
      getTransformedItems({
        items: _items,
        getInitials,
        getImageUrl,
      }),
    [_items]
  )

  const { visibleItems, foldedItems, restCountLabel, isViewAllNeeded } =
    useMemo(() => getOverflowValues(items), [items])

  const visibleItemsColors = useMemo(
    () => getColorOrder(visibleItems),
    [visibleItems]
  )

  return (
    <div ref={ref} {...props}>
      {isViewAllNeeded && !onClickViewAll && (
        <ViewAllModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          title={title}
          items={items}
        />
      )}
      <StyledWrapper>
        {visibleItems.map((item) => {
          return (
            <AvatarWithPopover
              colors={visibleItemsColors}
              item={item}
              key={`${item.name}_${item.id}`}
              size={size}
            />
          )
        })}
        {foldedItems.length > 0 && (
          <FoldedAvatarStack
            restCountLabel={restCountLabel as string}
            isViewAllNeeded={isViewAllNeeded}
            items={foldedItems}
            onClickViewAll={onClickViewAll || (() => setIsModalOpen(true))}
            size={size}
            title={title}
            viewMoreTriggerRef={
              !onClickViewAll ? viewMoreTriggerRef : undefined
            }
          />
        )}
      </StyledWrapper>
    </div>
  )
}

/**

 We use avatars to visually represent our users, places, and things in the app.
 These can be in the form of rich media or representative illustrations.

 @since 10.19.0

 @see [Storybook](https://procore.github.io/core/latest/?path=/story/demos-avatarstack--demo)

 @see [Design Guidelines](https://design.procore.com/avatar-stack)
 */
export const AvatarStack = forwardRef(_AvatarStack) as <
  Item extends AvatarStackItem
>(
  props: AvatarStackProps<Item> & { ref?: React.ForwardedRef<HTMLDivElement> }
) => ReturnType<typeof _AvatarStack>

// @ts-ignore
AvatarStack.displayName = 'AvatarStack'
